import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import * as styles from './PageIndicators.css';

interface IPageIndicatorsProps {
  /** Total number of items in the carousel. */
  totalItems: number;
  /** Index of the currently selected item. */
  selectedIndex: number;
  /** Number of items visible per page. */
  visibleCount: number;
  /** Callback when a page indicator is clicked. */
  onPageSelect: (pageIndex: number) => void;
}

function PageIndicators({
  totalItems,
  selectedIndex,
  visibleCount,
  onPageSelect,
}: Readonly<IPageIndicatorsProps>): JSX.Element | null {
  const { t } = useTranslation();
  const totalPages = Math.ceil(totalItems / visibleCount);

  if (totalPages <= 1) {
    return null;
  }

  const activePage = Math.min(Math.floor(selectedIndex / visibleCount), totalPages - 1);

  return (
    <div className={styles.root} role={'tablist'}>
      {Array.from({ length: totalPages }, (_, pageIndex) => (
        <button
          aria-label={t('Page {{page}}', { page: pageIndex + 1 })}
          aria-selected={pageIndex === activePage}
          className={[styles.indicator, pageIndex === activePage ? styles.indicatorActive : '']
            .join(' ')
            .trim()}
          data-name={`page-indicator-${pageIndex}`}
          key={pageIndex}
          onClick={(): void => onPageSelect(pageIndex)}
          role={'tab'}
          type={'button'}
        />
      ))}
    </div>
  );
}

export default PageIndicators;
